// src/components/kuestion/solo/SoloHome.jsx
//
// The Solo dashboard from the web mockup: where you stand against the pass
// line, four stats, what to revise tonight, the last five mocks against the
// pass line, and quick starts. Everything is derived from the existing
// single-user endpoints plus own strengths. The pass mark is the template's
// (v4 §6) — when a paper has none, the headline is the score alone.
import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSoloData } from './soloData';
import { Stat, RuleHead, Loading } from '../shared/Bits';
import { PlayIcon, TargetIcon, UploadIcon, FileIcon } from '../shared/Icons';
import { fmtDate } from '../shared/format';
import MocksChart from './MocksChart';
import { kq } from '../../../config/routes';

export default function SoloHome() {
  const navigate = useNavigate();
  const { templates, files, history, strengths } = useSoloData();

  const latest = history && history[0];
  const first = history && history[history.length - 1];
  const delta = latest && first && history.length > 1 ? Math.round(latest.pct - first.pct) : null;
  const banks = (files || []).filter((f) => f.is_valid);
  const questions = banks.reduce((n, f) => n + (Number(f.question_count) || 0), 0);
  const modules = strengths?.modules || [];
  const min = strengths?.min_questions_for_strength;
  const scored = modules.filter((m) => m.sufficient !== false && m.strength != null);
  const weakest = scored.length ? scored.reduce((a, b) => (Number(b.strength) < Number(a.strength) ? b : a)) : null;
  const thin = modules.filter((m) => m.sufficient === false);

  const hasMark = latest && latest.pass_percentage != null;
  const margin = hasMark ? Math.round(latest.pct - latest.pass_percentage) : null;
  const title = !history ? 'Loading your progress'
    : !latest ? 'Your first mock is waiting'
      : !hasMark ? `Your latest mock scored ${Math.round(latest.pct)}%`
        : margin >= 0 ? `You’re ${margin} point${margin === 1 ? '' : 's'} over the line` : `You’re ${-margin} point${margin === -1 ? '' : 's'} under the line`;

  // "What to revise tonight" — derived, so nothing here is invented.
  const revise = useMemo(() => {
    const out = [];
    if (weakest) out.push({ accent: true, title: `${weakest.module} is your weakest domain at ${Math.round(weakest.strength)}%`, sub: `${weakest.correct ?? '—'} of ${weakest.attempted ?? '—'} right so far. A short mock on it is the fastest way up.`, cta: 'Sit a mock', go: () => navigate(kq('exam')) });
    if (latest && latest.passed === false) out.push({ accent: true, title: `Your latest mock came in ${Math.round(latest.pass_percentage - latest.pct)} points under the pass line`, sub: `${latest.exam_name} on ${fmtDate(latest.at)} — ${latest.scored} of ${latest.total}. Retake it once you have revised.`, cta: 'Retake', go: () => navigate(kq('exam')) });
    if (history && history.length >= 2 && history[0].pct < history[1].pct) out.push({ title: `${history[0].exam_name} slipped ${Math.round(history[1].pct - history[0].pct)} points since your previous sitting`, sub: 'Review the explanations before going again.', cta: 'Progress', go: () => navigate(kq('progress')) });
    thin.slice(0, 1).forEach((m) => out.push({ title: `${m.module} has too little data to score`, sub: `${m.attempted || 0} question${m.attempted === 1 ? '' : 's'} attempted — ${min ?? 'a few'} are needed before a strength is reported.`, cta: 'Open', go: () => navigate(kq('progress')) }));
    return out.slice(0, 3);
  }, [weakest, latest, history, thin, min, navigate]);

  return (
    <div className="kqc-page">
      <div className="kqc-pagehead">
        <div style={{ minWidth: 0 }}>
          <div className="kqc-kicker" style={{ marginBottom: 8 }}>{history ? `${history.length} sitting${history.length === 1 ? '' : 's'} so far` : 'Solo'}</div>
          <h1 className="kqc-h1" style={{ fontSize: 'clamp(26px,3.6vw,44px)' }}>{title}</h1>
        </div>
        <span className="kqc-spacer" />
        <button type="button" className="btn btn-primary" onClick={() => navigate(kq('exam'))}>Sit a full mock</button>
      </div>

      <div className="kqc-stats" data-tour="solo-stats">
        <Stat label="Latest mock" value={latest ? `${Math.round(latest.pct)}%` : '—'} hint={latest ? `${hasMark ? `Pass mark ${latest.pass_percentage}%` : 'No pass mark'} · ${fmtDate(latest.at)}` : 'No sittings yet'} />
        <Stat label="Attempts" value={history ? history.length : '—'} hint={delta == null ? 'One to go on' : `${delta >= 0 ? '+' : ''}${delta} since the first`} />
        <Stat label="Weakest domain" value={weakest ? weakest.module : '—'} size="md" accent={weakest ? 'ink' : null} hint={weakest ? `${Math.round(weakest.strength)}% over ${weakest.attempted} questions` : 'Not enough data yet'} />
        <Stat label="Questions in your banks" value={files ? questions : '—'} hint={files ? `Across ${banks.length} bank${banks.length === 1 ? '' : 's'}` : ''} />
      </div>

      <div className="kqc-cols" style={{ gap: 0 }}>
        <div className="is-wide" style={{ paddingRight: 'clamp(0px,2vw,26px)' }}>
          <RuleHead title="What to revise tonight" />
          {history === null || strengths === null ? <Loading label="Working it out" /> : revise.length === 0 ? (
            <div className="kqc-empty">Nothing stands out yet. Sit a mock and this list fills itself in from your weakest domains and your last result.</div>
          ) : (
            <div className="kqc-list">
              {revise.map((a, i) => (
                <button type="button" key={i} className="kqc-list__row" onClick={a.go}>
                  <span className={`kqc-list__bar${a.accent ? ' is-accent' : ''}`} />
                  <span className="kqc-list__body">
                    <span className="kqc-list__title">{a.title}</span>
                    <span className="kqc-list__sub">{a.sub}</span>
                  </span>
                  <span className="kqc-list__cta">{a.cta} &rarr;</span>
                </button>
              ))}
            </div>
          )}

          <div style={{ margin: 'clamp(24px,3vw,34px) 0 14px' }}><RuleHead title="Your last five mocks" /></div>
          {history === null ? <Loading label="Loading" /> : history.length === 0 ? (
            <div className="kqc-empty">Your sittings chart against the pass line here.</div>
          ) : <MocksChart sittings={history.slice(0, 5).reverse()} />}
        </div>

        <div className="is-side is-side--rule">
          <RuleHead title="Start something" />
          <div className="kqc-quick" style={{ marginBottom: 'clamp(22px,3vw,30px)' }}>
            <button type="button" onClick={() => navigate(kq('exam'))}><PlayIcon />Sit a mock{templates ? ` · ${templates.length} template${templates.length === 1 ? '' : 's'}` : ''}</button>
            <button type="button" onClick={() => navigate(kq('exam'))}><TargetIcon />Drill one domain</button>
            <button type="button" onClick={() => navigate(kq('banks/manage'))}><UploadIcon />Upload a bank</button>
            <button type="button" onClick={() => navigate(kq('templates/build'))}><FileIcon />Build a template</button>
          </div>
          <div className="kqc-box kqc-box--surface">
            <div className="kqc-box__body">
              <div className="kqc-mono" style={{ fontSize: 14, marginBottom: 8 }}>Nobody is watching</div>
              <div className="kqc-small" style={{ color: 'var(--color-neutral-800)' }}>A solo account has no examiner, no cohort and no ranking. Every score here is yours alone, and every paper shows you the answers.</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
